import React, { useState, useRef, useEffect, useMemo } from 'react'
import { Steps, Button, Icon, Spin, Tooltip } from 'antd'
import './styles.scss'
import NewHeader from '../../components/NewHeader'
import UploadDocument from './components/UploadDocument'
import Voiceover from './components/Voiceover'
import ShotList from './components/ShotList'
import ImageGeneration from './components/ImageGeneration'
import VideoGeneration from './components/VideoGeneration'
import VideoStyle from './components/VideoStyle'

import {
  changeMachineState,
  createAiVideo,
  generateShots,
  getAiVideoById,
  groupShotsToScenes,
  updateAiVideo,
  getTaskStatus
} from '../../services/api/aiVideoGeneration'
import history from '../../history'
import _ from 'lodash'

const { Step } = Steps

const MAX_RETRIES = 10

const defaultValidation = {
  uploadDocument: false,
  voiceover: true,
  characterSelection: false,
  videoStyle: false,
  shotList: false,
  imageGeneration: false,
  videoGeneration: false
}

const defaultData = {
  file: null,
  voiceoverText: '',
  voiceoverAudio: null,
  videoStyle: {},
  selectedCharacter: null,
  characterList: [],
  shotList: [],
  shotLoading: false
}

const editValidation = {
  uploadDocument: true,
  voiceover: true,
  characterSelection: true,
  videoStyle: true,
  shotList: true,
  imageGeneration: true,
  videoGeneration: true
}
const VideoAi = (props) => {
  const [currentStep, setCurrentStep] = useState(0)
  // Create a ref to access the VideoGeneration component
  const videoGenerationRef = useRef(null)
  const imageGenerationRef = useRef(null)

  const [isFetchingVideoData, setIsFetchingVideoData] = useState(false)

  const [showProcessShots, setShowProcessShots] = useState(false)
  const [isFineTunedLoading, setIsFineTunedLoading] = useState(false)
  const [isSavingVideoData, setIsSavingVideoData] = useState(false)

  const [stepsValidation, setStepsValidation] = useState(defaultValidation)

  const [formData, setFormData] = useState(defaultData)

  const steps = [
    {
      key: 'uploadDocument',
      title: 'Upload',
      icon: 'file-text',
      component: UploadDocument
    },
    {
      key: 'voiceover',
      title: 'Voiceover',
      icon: 'sound',
      component: Voiceover
    },
    {
      key: 'videoStyle',
      title: 'Video Style',
      icon: 'camera',
      component: VideoStyle
    },
    {
      key: 'shotList',
      title: 'Shot List',
      icon: 'ordered-list',
      component: ShotList
    },
    {
      key: 'imageGeneration',
      title: 'Images',
      icon: 'picture',
      component: ImageGeneration
    },
    {
      key: 'videoGeneration',
      title: 'Video',
      icon: 'video-camera',
      component: VideoGeneration
    }
  ]

  const { videoId, handleGoBack } = props

  useEffect(() => {
    if (videoId) {
      fetchVideoData()
    }
  }, [videoId])

  useEffect(() => {
    handleSaveVideoData()
  }, [formData])

  const handleSaveVideoData = () => {
    if (isSavingVideoData) {
      _.debounce(handleSaveVideoData, 1000)
      return
    }

    if (videoId) {
      handleUpdateVideoData(formData)
    } else {
      handleCreateNew(formData)
    }
  }

  const handleCreateNew = async data => {
    // If data is the same as default, do nothing
    if (_.isEqual(data, defaultData)) {
      return
    }

    try {
      setIsSavingVideoData(true)
      const payload = getPayload(data)
      const response = await createAiVideo(payload)
      const videoId = response && response.data && response.data.id
      history.push(`/video-create/${videoId}`)
    } catch (error) {
      console.error('Error creating new video:', error)
    } finally {
      setIsSavingVideoData(false)
    }
  }

  const handleUpdateVideoData = async data => {
    if (_.isEqual(data, defaultData)) {
      return
    }

    try {
      setIsSavingVideoData(true)
      const payload = getPayload(data)
      const response = await updateAiVideo(videoId, payload)

      console.log('Response:', response)
    } catch (error) {
      console.error('Error updating video data:', error)
    } finally {
      setIsSavingVideoData(false)
    }
  }

  const getPayload = data => {
    const payload = {
      voiceoverText: data.voiceoverText,
      voiceoverAudio: data.voiceoverAudio,
      videoStyle: data.videoStyle,
      selectedCharacter: data.selectedCharacter,
      shotList: data.shotList,
      video_url: data.finalVideo || '',
      characterList: [],
      file: null
    }
    return payload
  }

  const fetchVideoData = async () => {
    if (isFetchingVideoData) {
      return
    }

    try {
      setIsFetchingVideoData(true)
      console.log('Fetching video data for videoId:', videoId)
      const response = await getAiVideoById(videoId)
      const data = response && response.data
      setFormData(prev => ({
        ...prev,
        shotList: data.shot_list,
        voiceoverText: data.voiceover_text,
        selectedCharacter: data.selected_character,
        videoStyle: data.video_style,
        shotsApiRes: flatShots(data.shot_list),
        finalVideo: data.video_url || ''
      }))
      setStepsValidation({
        uploadDocument: Boolean(data.voiceover_text),
        voiceover: true,
        videoStyle: Boolean(data.video_style),
        shotList:
          data.shot_list &&
          data.shot_list.length > 0 &&
          data.selected_character &&
          data.selected_character.length > 0,
        imageGeneration:
          data.shot_list.length > 0 &&
          data.shot_list.every(
            scene =>
              scene.shots.length > 0 && scene.shots.every(shot => shot.imageSrc)
          ),
        videoGeneration:
          data.shot_list.length > 0 &&
          data.shot_list.every(scene => scene.shots.length > 0) &&
          data.shot_list.every(scene => scene.shots.every(shot => shot.vidSrc))
      })

      // change route to video-create/id
      window.history.pushState(null, '', `/video-create/${videoId}`)
    } catch (error) {
      console.error('Error fetching video data:', error)
    } finally {
      setIsFetchingVideoData(false)
    }
  }

  const flatShots = input =>
    input.reduce((acc, scene) => {
      scene.shots.forEach(shot => {
        // Replace spaces with hyphens in the key (e.g., "shot 1" -> "shot-1")
        const formattedKey = shot.key.replace(/\s+/g, '-')
        acc[formattedKey] = shot
      })
      return acc
    }, {})

  const handleNext = () => {
    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1))
  }

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0))
  }

  const handleGenerateFullVideo = () => {
    // Call the generate function from the VideoGeneration component using the ref
    if (
      videoGenerationRef.current &&
      videoGenerationRef.current.generateFinalVideo
    ) {
      videoGenerationRef.current.generateFinalVideo()
    } else {
      console.error('Video generation function not available')
    }
  }

  const handleProcessShots = () => {
    // Call the processShots function from the ImageGeneration component using the ref
    if (imageGenerationRef.current && imageGenerationRef.current.processShots) {
      imageGenerationRef.current.processShots()
    } else {
      console.error('Image generation function not available')
    }
  }

  useEffect(() => {
    if (formData.voiceoverText && formData.shotList.length === 0) {
      fetchShots()
    }
  }, [formData.voiceoverText])

  const fetchShots = async (retryCount = 0) => {
    setFormData(prev => ({ ...prev, shotLoading: true }))
    const storyText = formData && formData.voiceoverText

    try {
      const res = await generateShots({ story: storyText })

      console.log('Shots response:', res)
      const shotsData = res.data
      handleScenesMapping(shotsData)
    } catch (err) {
      if (retryCount < MAX_RETRIES) {
        // Retry after a short delay
        setTimeout(() => fetchShots(retryCount + 1), 1000)
      } else {
        setFormData(prev => ({ ...prev, shotLoading: false }))
      }
    }
  }

  const handleScenesMapping = async (data, isFineTuned) => {
    try {
      const payload = {
        input_json_data: data
      }

      const response = await groupShotsToScenes(payload)
      
      // Check if response contains task_id (async processing)
      if (response.data && response.data.task_id) {
        await pollTaskStatus(response.data.task_id, data)
      } else {
        // Direct response with output
        const sceneData = response.data && response.data.output
        const formattedSceneData = formatSceneObj(sceneData)

        setFormData(prev => ({
          ...prev,
          shotList: formattedSceneData,
          shotsApiRes: data
        }))
      }
    } catch (error) {
      console.error('Scene mapping failed:', error)
    } finally {
      setFormData(prev => ({ ...prev, shotLoading: false }))
    }
  }

  const pollTaskStatus = async (taskId, originalData, retryCount = 0) => {
    try {
      const response = await getTaskStatus(taskId)
      
      if (response.data && response.data.status === 'success' && response.data.output) {
        // Task completed successfully
        const sceneData = response.data.output
        const formattedSceneData = formatSceneObj(sceneData)

        setFormData(prev => ({
          ...prev,
          shotList: formattedSceneData,
          shotsApiRes: originalData
        }))
      } else if (response.data && response.data.status === 'failed') {
        // Task failed
        console.error('Task failed:', response.data)
        throw new Error('Task processing failed')
      } else if (retryCount < MAX_RETRIES) {
        // Task still processing, retry after delay
        setTimeout(() => pollTaskStatus(taskId, originalData, retryCount + 1), 2000)
      } else {
        // Max retries exceeded
        console.error('Max retries exceeded for task:', taskId)
        throw new Error('Task processing timeout - max retries exceeded')
      }
    } catch (error) {
      if (retryCount < MAX_RETRIES) {
        // Retry on error after delay
        setTimeout(() => pollTaskStatus(taskId, originalData, retryCount + 1), 2000)
      } else {
        console.error('Error polling task status:', error)
        throw error
      }
    }
  }

  const formatSceneObj = sceneObj => {
    return Object.entries(sceneObj).map(([sceneKey, sceneValue]) => {
      const { scene_location = '', shots = [] } = sceneValue

      // Format individual shots within the scene
      const formattedShots = shots.map((shotObj, index) => {
        // Extract the actual shot data - each item in shots array is an object with a single key like "shot 1"
        const shotKey = Object.keys(shotObj)[0]
        const shotValue = shotObj[shotKey]

        return {
          id: `${sceneKey}-shot-${index + 1}`,
          key: shotKey,
          title: shotValue.title || '',
          location: shotValue.location || '',
          scenery: shotValue.scenery || '',
          shot_direction: shotValue['shot direction'] || '',
          dialogue: shotValue.dialogue || {},
          characters: shotValue.characters || [] // Preserve characters array
        }
      })

      return {
        id: sceneKey,
        title: sceneKey,
        description: scene_location,
        shots: formattedShots
      }
    })
  }

  const updateStepValidation = (key, isValid) => {
    setStepsValidation(prev => ({
      ...prev,
      [key]: isValid
    }))
  }

  const renderStepContent = () => {
    const { key, component: StepComponent } = steps[currentStep]

    // For the last step (VideoGeneration), pass the ref
    if (currentStep === steps.length - 1) {
      return (
        <StepComponent
          ref={videoGenerationRef}
          icon={steps[currentStep].icon}
          formData={formData}
          setFormData={setFormData}
          onValidationChange={isValid => updateStepValidation(key, isValid)}
          onProceed={handleNext}
        />
      )
    }

    // For the last step (ImageGeneration), pass the ref
    if (currentStep === steps.length - 3) {
      return (
        <StepComponent
          ref={imageGenerationRef}
          icon={steps[currentStep].icon}
          formData={formData}
          setFormData={setFormData}
          onValidationChange={isValid => updateStepValidation(key, isValid)}
          onProceed={handleNext}
          onEnableProcessShots={() => setShowProcessShots(true)}
          onDisableProcessShots={() => setShowProcessShots(false)}
          onFineTunedLoading={() => setIsFineTunedLoading(prev => !prev)}
        />
      )
    }

    // For other steps, render normally
    return StepComponent ? (
      <StepComponent
        icon={steps[currentStep].icon}
        formData={formData}
        setFormData={setFormData}
        onValidationChange={isValid => updateStepValidation(key, isValid)}
        onProceed={handleNext}
      />
    ) : null
  }

  const getPreviousNextStepTitles = () => {
    const previousStepIndex = Math.max(currentStep - 1, 0)
    const nextStepIndex = Math.min(currentStep + 1, steps.length - 1)

    return {
      previousStepTitle: steps[previousStepIndex].title,
      nextStepTitle: steps[nextStepIndex].title
    }
  }

  const shouldShowNextButton = () => {
    // Don't show Next button on the last step (VideoGeneration)
    if (currentStep === steps.length - 1) return false

    // For ImageGeneration step (steps.length - 3), only show if showProcessShots is false
    if (currentStep === steps.length - 3) return !showProcessShots

    // For all other steps, show the Next button
    return true
  }

  const startMachine = async () => {
    await changeMachineState(1)
  }

  const stopMachine = async () => {
    await changeMachineState(0)
  }

  const isAllScenesGenerated = formData.shotList.every(scene =>
    scene.shots.every(shot => shot.vidSrc)
  )

  console.log({ formData, showProcessShots })

  return (
    <Spin spinning={isFetchingVideoData}>
      <div className="video-generation">
        <div className="video-generation__header">
          <div className="video-generation__header_info">
            <div
              className="video-generation__header_info-icon"
              onClick={handleGoBack}
            >
              <Tooltip title="Back to Video List">
                <Icon type="arrow-left" />
              </Tooltip>
            </div>

            <div className="video-generation__header_title">
              <h1>Create Video</h1>
              <p>Follow the steps below to generate your video</p>
            </div>
          </div>
          <NewHeader />
        </div>

        {/* <div className="machine_control_buttons">
          <Button type="primary" size="large" onClick={startMachine}>
            Start Machine
          </Button>
          <Button type="primary" size="large" onClick={stopMachine}>
            Stop Machine
          </Button>
        </div> */}

        <div className="video-generation__container">
          <Steps
            current={currentStep}
            labelPlacement="vertical"
            className="video-generation__steps"
          >
            {steps.map((step, index) => (
              <Step
                key={step.key}
                title={step.title}
                icon={
                  <div
                    className={`step-icon ${
                      currentStep === index
                        ? 'current'
                        : currentStep > index
                        ? 'completed'
                        : ''
                    }`}
                  >
                    <Icon type={step.icon} />
                  </div>
                }
              />
            ))}
          </Steps>

          <div className="video-generation__content">{renderStepContent()}</div>
          <div className="video-generation__navigation">
            <div className="video-generation__navigation-left">
              {currentStep > 0 && (
                <Button
                  size="large"
                  onClick={handlePrevious}
                  className="nav-button"
                >
                  <Icon type="left" />
                  {getPreviousNextStepTitles().previousStepTitle}
                </Button>
              )}
            </div>
            <div className="video-generation__navigation-right">
              {currentStep > 0 && (
                <Button
                  size="large"
                  loading={isSavingVideoData}
                  onClick={handleSaveVideoData}
                >
                  {isSavingVideoData ? 'Saving...' : 'Save'}
                </Button>
              )}
              {shouldShowNextButton() && (
                <Button
                  type="primary"
                  size="large"
                  onClick={handleNext}
                  disabled={
                    currentStep === steps.length - 1 ||
                    !stepsValidation[steps[currentStep].key]
                  }
                  className="nav-button"
                >
                  Next: {getPreviousNextStepTitles().nextStepTitle}
                  <Icon type="right" />
                </Button>
              )}

              {currentStep === steps.length - 3 && showProcessShots && (
                <Button
                  size="large"
                  onClick={handleProcessShots}
                  className="nav-button"
                  type="primary"
                  loading={isFineTunedLoading}
                >
                  Process Shots <Icon type="right" />
                </Button>
              )}

              {currentStep === steps.length - 1 && (
                <Button
                  type="primary"
                  size="large"
                  shape="round"
                  icon="play-circle"
                  disabled={!isAllScenesGenerated}
                  onClick={handleGenerateFullVideo}
                >
                  Generate Full Video
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Spin>
  )
}

export default VideoAi
